import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Inbox, Settings as SettingsIcon, Users, Sliders, MessageSquare, Sparkles, Loader2, Plus, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  open: "bg-amber-500/15 text-amber-700 border-amber-300",
  acknowledged: "bg-blue-500/15 text-blue-700 border-blue-300",
  resolved: "bg-emerald-500/15 text-emerald-700 border-emerald-300",
  archived: "bg-slate-500/15 text-slate-700 border-slate-300",
  outbound: "bg-violet-500/15 text-violet-700 border-violet-300",
};

const STATUS_LABEL = { outbound: "Sent" };

const CHANNEL_LABELS = {
  email: "Email",
  portal_push: "Portal Push",
};

export default function AdminCommunications() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("inbox");
  const [requests, setRequests] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterAudience, setFilterAudience] = useState("all");
  const [selected, setSelected] = useState(null);
  const [statusNotes, setStatusNotes] = useState("");
  const [types, setTypes] = useState([]);
  const [policy, setPolicy] = useState([]);
  const [newType, setNewType] = useState({ name: "", description: "", audience: "employee_portal" });
  const [aiBusy, setAiBusy] = useState(false);
  const [aiTone, setAiTone] = useState("warm");
  // New-message composer (admin → portal users)
  const [composerOpen, setComposerOpen] = useState(false);
  const [composer, setComposer] = useState({
    audience: "employee_portal",
    target: "all",
    subject: "",
    body: "",
    send_email: false,
  });
  const [composerRecipients, setComposerRecipients] = useState([]);
  const [composerSending, setComposerSending] = useState(false);

  const aiSuggestReply = async (status) => {
    if (!selected) return;
    setAiBusy(true);
    try {
      const res = await axios.post(`${API}/ai-writer/suggest-reply`, {
        request_subject: selected.subject,
        request_body: selected.body || "",
        request_type_key: selected.request_type_key,
        audience: selected.audience,
        tone: aiTone,
        desired_status: status,
      });
      setStatusNotes(res.data.text);
      toast.success(`AI reply (${aiTone}) drafted`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI failed");
    } finally {
      setAiBusy(false);
    }
  };

  const loadAll = async () => {
    try {
      const [r, t, p] = await Promise.all([
        axios.get(`${API}/comm/requests`),
        axios.get(`${API}/comm/request-types`),
        axios.get(`${API}/comm/policy`),
      ]);
      setRequests(r.data.requests || []);
      setOpenCount(r.data.open_count || 0);
      setTypes(t.data.types || []);
      setPolicy(p.data.policy || []);
    } catch (e) {
      toast.error("Failed to load communications data");
    }
  };

  useEffect(() => { loadAll(); }, []);

  // Load recipient list when composer audience changes
  useEffect(() => {
    if (!composerOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/comm/outbound/recipients`, { params: { audience: composer.audience } });
        if (!cancelled) setComposerRecipients(r.data.recipients || []);
      } catch {
        if (!cancelled) setComposerRecipients([]);
      }
    })();
    return () => { cancelled = true; };
  }, [composerOpen, composer.audience]);

  const sendComposer = async () => {
    if (!composer.subject.trim()) {
      toast.error("Subject is required");
      return;
    }
    setComposerSending(true);
    try {
      const r = await axios.post(`${API}/comm/outbound`, composer);
      toast.success(
        `Sent to ${r.data.delivered_portal_push} portal user${r.data.delivered_portal_push === 1 ? "" : "s"}` +
        (composer.send_email ? ` (${r.data.delivered_email} emails)` : "")
      );
      setComposerOpen(false);
      setComposer({ audience: composer.audience, target: "all", subject: "", body: "", send_email: false });
      // Refresh inbox so the new "Sent" thread shows up under the Sent filter
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setComposerSending(false);
    }
  };

  const filtered = useMemo(() => {
    return requests.filter((r) => {
      if (filterStatus !== "all" && r.status !== filterStatus) return false;
      if (filterAudience !== "all" && r.audience !== filterAudience) return false;
      return true;
    });
  }, [requests, filterStatus, filterAudience]);

  const setStatus = async (id, status) => {
    try {
      await axios.put(`${API}/comm/requests/${id}/status`, { status, notes: statusNotes });
      toast.success(`Marked ${status}`);
      setSelected(null);
      setStatusNotes("");
      loadAll();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const createType = async () => {
    if (!newType.name.trim()) return toast.error("Name required");
    try {
      await axios.post(`${API}/comm/request-types`, { ...newType, is_active: true });
      toast.success("Request type created");
      setNewType({ name: "", description: "", audience: "employee_portal" });
      loadAll();
    } catch (e) {
      toast.error("Failed to create type");
    }
  };

  const toggleType = async (t) => {
    try {
      await axios.put(`${API}/comm/request-types/${t.id}`, { ...t, is_active: !t.is_active });
      loadAll();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const togglePolicy = async (row, next) => {
    try {
      await axios.put(`${API}/comm/policy`, { audience: row.audience, channel: row.channel, is_available: next });
      loadAll();
    } catch (e) {
      toast.error("Failed to update policy");
    }
  };

  return (
    <div className="space-y-6 p-6" data-testid="admin-communications">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <MessageSquare className="h-7 w-7 text-emerald-600" />
            Communications
          </h1>
          <p className="text-muted-foreground mt-1">Employee & client requests, channel policy, and request types.</p>
        </div>
        <div className="flex items-center gap-2">
          {openCount > 0 && (
            <Badge variant="outline" className="border-amber-400 text-amber-800 bg-amber-50">
              {openCount} open request{openCount === 1 ? "" : "s"}
            </Badge>
          )}
          <Button
            onClick={() => setComposerOpen(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="comm-new-message-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> New Message
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="inbox" data-testid="comm-tab-inbox"><Inbox className="h-4 w-4 mr-1" /> Inbox</TabsTrigger>
          <TabsTrigger value="types" data-testid="comm-tab-types"><Users className="h-4 w-4 mr-1" /> Request Types</TabsTrigger>
          <TabsTrigger value="policy" data-testid="comm-tab-policy"><Sliders className="h-4 w-4 mr-1" /> Channel Policy</TabsTrigger>
        </TabsList>

        <TabsContent value="inbox" className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <select className="border rounded-md px-3 py-2 text-sm" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="filter-status">
              <option value="all">All statuses</option>
              <option value="open">Open</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="resolved">Resolved</option>
              <option value="archived">Archived</option>
              <option value="outbound">Sent (by me)</option>
            </select>
            <select className="border rounded-md px-3 py-2 text-sm" value={filterAudience} onChange={(e) => setFilterAudience(e.target.value)} data-testid="filter-audience">
              <option value="all">All audiences</option>
              <option value="employee_portal">Employees</option>
              <option value="client_portal">Clients</option>
            </select>
          </div>
          {filtered.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-muted-foreground" data-testid="comm-empty">No requests match this filter.</CardContent></Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2 md:col-span-1" data-testid="comm-request-list">
                {filtered.map((r) => (
                  <Card key={r.id} onClick={() => { setSelected(r); setStatusNotes(r.notes || ""); }} className={`cursor-pointer transition hover:border-emerald-400 ${selected?.id === r.id ? "border-emerald-500" : ""}`} data-testid={`comm-request-card-${r.id}`}>
                    <CardContent className="p-4 space-y-1">
                      <div className="flex items-start justify-between">
                        <p className="font-medium text-sm leading-snug">{r.subject}</p>
                        <Badge variant="outline" className={STATUS_COLORS[r.status]}>{STATUS_LABEL[r.status] || r.status}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">{r.audience.replace("_portal", "")} • {r.request_type_key} • {new Date(r.created_at).toLocaleString()}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="md:col-span-2">
                {selected ? (
                  <Card data-testid="comm-request-detail">
                    <CardHeader>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <CardTitle className="text-lg">{selected.subject}</CardTitle>
                          <p className="text-xs text-muted-foreground mt-1">{selected.audience.replace("_portal", "")} • {selected.request_type_key} • {new Date(selected.created_at).toLocaleString()}</p>
                        </div>
                        <Badge variant="outline" className={STATUS_COLORS[selected.status]}>{STATUS_LABEL[selected.status] || selected.status}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {selected.status === "outbound" && (() => {
                        let meta = {};
                        try { meta = JSON.parse(selected.attachments_json || "{}"); } catch { /* ignore */ }
                        return (
                          <div className="rounded-md border bg-violet-50/40 border-violet-200 px-3 py-2 text-xs" data-testid="comm-outbound-meta">
                            <span className="font-medium text-violet-800">Sent to:</span>{" "}
                            <span>{meta.target_label || `${meta.recipient_count || 0} recipients`}</span>
                            <span className="text-muted-foreground">
                              {" · "}{meta.delivered_portal_push || 0} portal{meta.send_email_requested ? ` · ${meta.delivered_email || 0} email` : ""}
                            </span>
                          </div>
                        );
                      })()}
                      <div>
                        <p className="text-xs font-medium text-muted-foreground mb-1">{selected.status === "outbound" ? "Message" : "Body"}</p>
                        <p className="whitespace-pre-wrap text-sm">{selected.body || "—"}</p>
                      </div>
                      {selected.status !== "outbound" && (
                        <>
                          <div>
                            <div className="flex items-center justify-between mb-1 gap-2 flex-wrap">
                              <p className="text-xs font-medium text-muted-foreground">Internal notes / reply</p>
                              <div className="flex items-center gap-1">
                                <select value={aiTone} onChange={(e) => setAiTone(e.target.value)} className="border rounded-md px-2 py-1 text-xs h-7" data-testid="comm-ai-tone-select">
                                  <option value="warm">Warm</option>
                                  <option value="professional">Professional</option>
                                  <option value="concise">Concise</option>
                                  <option value="formal">Formal</option>
                                  <option value="empathetic">Empathetic</option>
                                </select>
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => aiSuggestReply("acknowledged")} disabled={aiBusy} data-testid="ai-suggest-reply-btn">
                                  {aiBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Sparkles className="h-3 w-3 mr-1 text-emerald-600" />}
                                  AI Suggest
                                </Button>
                              </div>
                            </div>
                            <Textarea value={statusNotes} onChange={(e) => setStatusNotes(e.target.value)} rows={3} placeholder="Add a note that's sent back to the requester" data-testid="comm-notes-input" />
                          </div>
                          <div className="flex flex-wrap gap-2">
                            <Button size="sm" variant="outline" onClick={() => setStatus(selected.id, "acknowledged")} data-testid="comm-acknowledge-btn">Acknowledge</Button>
                            <Button size="sm" onClick={() => setStatus(selected.id, "resolved")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="comm-resolve-btn">Resolve</Button>
                            <Button size="sm" variant="ghost" onClick={() => setStatus(selected.id, "archived")} data-testid="comm-archive-btn">Archive</Button>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card><CardContent className="p-8 text-center text-muted-foreground">Select a request to view detail.</CardContent></Card>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="types" className="space-y-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Create new request type</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <Input placeholder="Name (e.g. Vehicle Issue)" value={newType.name} onChange={(e) => setNewType({ ...newType, name: e.target.value })} data-testid="new-type-name" />
              <Input placeholder="Description" value={newType.description} onChange={(e) => setNewType({ ...newType, description: e.target.value })} data-testid="new-type-desc" />
              <select className="border rounded-md px-3 py-2 text-sm" value={newType.audience} onChange={(e) => setNewType({ ...newType, audience: e.target.value })} data-testid="new-type-audience">
                <option value="employee_portal">Employees</option>
                <option value="client_portal">Clients</option>
              </select>
              <Button onClick={createType} data-testid="create-type-btn">Add Type</Button>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {["employee_portal", "client_portal"].map((aud) => (
              <Card key={aud}>
                <CardHeader><CardTitle className="text-base">{aud === "employee_portal" ? "Employee" : "Client"} Types</CardTitle></CardHeader>
                <CardContent className="space-y-2" data-testid={`types-list-${aud}`}>
                  {types.filter((t) => t.audience === aud).map((t) => (
                    <div key={t.id} className="flex items-center justify-between p-2 border rounded-md">
                      <div>
                        <p className="text-sm font-medium">{t.name} {t.is_default && <Badge variant="outline" className="ml-1 text-xs">default</Badge>}</p>
                        <p className="text-xs text-muted-foreground">{t.description || t.key}</p>
                      </div>
                      <Switch checked={t.is_active} onCheckedChange={() => toggleType(t)} data-testid={`toggle-type-${t.id}`} />
                    </div>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="policy" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Channel Policy</CardTitle>
              <p className="text-xs text-muted-foreground">Toggle which channels are *available* to portal users. Each user still has their own opt-in.</p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="policy-grid">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-2 px-3">Audience</th>
                      <th className="py-2 px-3">Email</th>
                      <th className="py-2 px-3">Portal Push</th>
                    </tr>
                  </thead>
                  <tbody>
                    {["employee_portal", "client_portal"].map((aud) => (
                      <tr key={aud} className="border-b">
                        <td className="py-2 px-3 font-medium">{aud === "employee_portal" ? "Employees" : "Clients"}</td>
                        {["email", "portal_push"].map((ch) => {
                          const row = policy.find((p) => p.audience === aud && p.channel === ch) || { audience: aud, channel: ch, is_available: true };
                          return (
                            <td key={ch} className="py-2 px-3">
                              <Switch checked={row.is_available} onCheckedChange={(v) => togglePolicy(row, v)} data-testid={`policy-${aud}-${ch}`} />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New-message composer dialog: admin pushes a message to portal users */}
      <Dialog open={composerOpen} onOpenChange={setComposerOpen}>
        <DialogContent className="max-w-lg" data-testid="comm-composer-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-emerald-600" /> New Message
            </DialogTitle>
            <DialogDescription>
              Send a message directly to one or all of your portal users. Goes to their notification bell instantly; toggle below to also send an email.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Audience</label>
                <select
                  className="w-full mt-1 border rounded-md h-9 px-2 text-sm bg-background"
                  value={composer.audience}
                  onChange={(e) => setComposer({ ...composer, audience: e.target.value, target: "all" })}
                  data-testid="composer-audience"
                >
                  <option value="employee_portal">Employees</option>
                  <option value="client_portal">Clients</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Recipient</label>
                <select
                  className="w-full mt-1 border rounded-md h-9 px-2 text-sm bg-background"
                  value={composer.target}
                  onChange={(e) => setComposer({ ...composer, target: e.target.value })}
                  data-testid="composer-target"
                >
                  <option value="all">Everyone in this group ({composerRecipients.length})</option>
                  {composerRecipients.map((r) => (
                    <option key={r.id} value={r.id}>{r.name} — {r.email}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Subject</label>
              <Input
                value={composer.subject}
                onChange={(e) => setComposer({ ...composer, subject: e.target.value })}
                placeholder="e.g. Schedule change for Friday"
                maxLength={200}
                data-testid="composer-subject"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Message</label>
              <Textarea
                rows={6}
                value={composer.body}
                onChange={(e) => setComposer({ ...composer, body: e.target.value })}
                placeholder="Type your message…"
                maxLength={5000}
                data-testid="composer-body"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={composer.send_email}
                onCheckedChange={(v) => setComposer({ ...composer, send_email: v })}
                data-testid="composer-send-email"
              />
              <label className="text-sm">Also send email</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setComposerOpen(false)} data-testid="composer-cancel">Cancel</Button>
            <Button
              onClick={sendComposer}
              disabled={composerSending || !composer.subject.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="composer-send"
            >
              {composerSending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Send className="h-4 w-4 mr-2" /> Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
